// Utility to prevent special character input for numeric fields
// Allows only numbers and a single dot (for decimals), blocks all other characters


// Use onKeyDown to block invalid characters and preserve the current value
export function preventSpecialCharKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
  const allowedKeys = [
    'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End',
  ];
  // Allow: digits, one dot (if not already present), navigation keys
  if (
    allowedKeys.includes(e.key) ||
    (e.ctrlKey || e.metaKey) // Allow copy, paste, select all, etc.
  ) {
    return;
  }
  // Allow one dot if not present
  if (e.key === '.') {
    const value = (e.currentTarget as HTMLInputElement).value;
    if (value.includes('.')) {
      e.preventDefault();
    }
    return;
  }
  // Block anything that's not a digit
  if (!/\d/.test(e.key)) {
    e.preventDefault();
  }
}

// Optionally, you can add a version that allows only integers:
export function preventNonNumericInput(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) {
  const input = e.currentTarget;
  input.value = input.value.replace(/\D/g, "");
}

//prevent user to enter special characters in text fields
export const preventSpecialCharKeyDownInTextfield = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z0-9 ]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

export const preventSpecialCharKeyDownInTextfieldAllowPlus = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z0-9 +]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

export const preventSpecialCharKeyAndNumbersDownInTextfield = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z ]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Prevents users from entering spaces in input fields (e.g. email).
 */
export const preventSpaceInput = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

/**
 * Removes all spaces from pasted input (useful for email fields).
 */
export const removeSpacesFromInput = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const input = e.currentTarget;
  const valueWithoutSpaces = input.value.replace(/\s+/g, "");

  if (input.value !== valueWithoutSpaces) {
    const cursor = input.selectionStart ?? valueWithoutSpaces.length;

    input.value = valueWithoutSpaces;

    requestAnimationFrame(() => {
      input.setSelectionRange(cursor, cursor);
    });
  }
};

/**
 * Prevents users from entering special characters.
 * Allows only letters, numbers, space, comma, period, @, +, ', and /
 */
export const preventInvalidTextInput = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // Allow navigation keys
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
    return;
  }

  const regex = /^[a-zA-Z0-9\s,.@+'/]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Removes invalid characters from pasted or autofilled text.
 */
export const removeInvalidTextCharacters = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const input = e.currentTarget;

  const sanitizedValue = input.value.replace(
    /[^a-zA-Z0-9\s,.@+'/]/g,
    ""
  );

  if (input.value !== sanitizedValue) {
    input.value = sanitizedValue;
  }
};

/**
 * Validates if the input is a valid website URL ending with .com, .in, or .org
 */
export const isValidWebsite = (value: string): boolean => {
  if (!value) return true;

  // Matches optional http/https, optional www, domain name, and strictly .com, .in, or .org TLDs
  // Also allows optional paths at the end (e.g., .com/about)
  const websiteRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.(com|in|org)(\/.*)?$/i;

  return websiteRegex.test(value);
};

/**
 * Prevents users from typing spaces and invalid URL characters in the website field.
 */
export const preventInvalidURLCharKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // Allow navigation/control keys
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
    return;
  }

  // Allow only letters, numbers, and standard URL characters (-, ., :, /, _, ?, =, &)
  const regex = /^[a-zA-Z0-9\-.:/_?=&]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};


export const preventSpaceInputInField = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.target as HTMLInputElement;
  if (target.value.includes(" ")) {
    target.value = target.value.replace(/\s/g, "");
  }
};